'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { interactionListMachine } from './machines/interactionListMachine'
import { NexusCrm } from '@open/nexus-public-api'
import { mapLibraryStatusToUi, mapLibraryPriorityToUi } from './types'
import type {
  Interaction,
  InteractionMessage,
  InteractionNote,
  InteractionActivity,
  InteractionFilters,
  InteractionStatistics,
} from './types'
import type { TicketAnalysis } from '@repo/ai-analytics'

export type InteractionListMachineApi = ReturnType<typeof useMachine<typeof interactionListMachine>>

const InteractionListMachineContext = createContext<InteractionListMachineApi | null>(null)

export function InteractionListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(interactionListMachine)
  return (
    <InteractionListMachineContext.Provider value={api}>
      {children}
    </InteractionListMachineContext.Provider>
  )
}

export function useInteractionListMachine() {
  const ctx = useContext(InteractionListMachineContext)
  if (!ctx) {
    throw new Error('useInteractionListMachine must be used within InteractionListMachineProvider')
  }
  return ctx
}

export function useInteractionListMachineOptional() {
  return useContext(InteractionListMachineContext)
}

function computeStatistics(interactions: Interaction[]): InteractionStatistics {
  return {
    total: interactions.length,
    active: interactions.filter((i) => mapLibraryStatusToUi(i.status) === 'active').length,
    waiting: interactions.filter((i) => mapLibraryStatusToUi(i.status) === 'waiting').length,
    closed: interactions.filter((i) => mapLibraryStatusToUi(i.status) === 'closed').length,
    transferred: interactions.filter(
      (i) => i.status === NexusCrm.CrmInteractionStatusNative.resolved && i.tags?.includes('transferred')
    ).length,
    byType: {
      chat: interactions.filter((i) => i.type === 'chat').length,
      call: interactions.filter((i) => i.type === 'call').length,
      email: interactions.filter((i) => i.type === 'email').length,
    },
    byPriority: {
      urgent: interactions.filter(
        (i) => i.priority && mapLibraryPriorityToUi(i.priority) === 'urgent'
      ).length,
      high: interactions.filter((i) => i.priority && mapLibraryPriorityToUi(i.priority) === 'high')
        .length,
      normal: interactions.filter(
        (i) => i.priority && mapLibraryPriorityToUi(i.priority) === 'normal'
      ).length,
      low: interactions.filter((i) => i.priority && mapLibraryPriorityToUi(i.priority) === 'low')
        .length,
    },
    sla: {
      withinSLA: interactions.filter((i) => !i.slaWarning && !i.slaExpired).length,
      atRisk: interactions.filter((i) => i.slaWarning).length,
      breached: interactions.filter((i) => i.slaExpired).length,
    },
    averageResponseTime: 15,
    averageHandleTime: 25,
    averageWaitTime: 5,
  }
}

export function useInteractionListStoreCompat() {
  const machineApi = useInteractionListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context

    const updateStatistics = () => {
      const currentCtx = machineApi[0].context
      const stats = computeStatistics(currentCtx.interactions)
      machineApi[1]({ type: 'SET_STATISTICS', statistics: stats })
    }

    return {
      interactions: ctx.interactions,
      messages: ctx.messages,
      notes: ctx.notes,
      activities: ctx.activities,
      analysisCache: ctx.analysisCache,
      analyzingIds: ctx.analyzingIds,
      selectedInteractionId: ctx.selectedInteractionId,
      filters: ctx.filters,
      isLoading: ctx.isLoading,
      error: ctx.error,
      statistics: ctx.statistics,
      setAnalysisResult: (interactionId: string, analysis: TicketAnalysis) =>
        send({ type: 'SET_ANALYSIS_RESULT', interactionId, analysis }),
      setAnalyzing: (interactionId: string, analyzing: boolean) =>
        send({ type: 'SET_ANALYZING', interactionId, analyzing }),
      getAnalysis: (interactionId: string) => ctx.analysisCache[interactionId],
      setInteractions: (interactions: Interaction[]) =>
        send({ type: 'SET_INTERACTIONS', interactions }),
      addInteraction: (interaction: Interaction) => send({ type: 'ADD_INTERACTION', interaction }),
      updateInteraction: (id: string, updates: Partial<Interaction>) =>
        send({ type: 'UPDATE_INTERACTION', id, updates }),
      removeInteraction: (id: string) => send({ type: 'REMOVE_INTERACTION', id }),
      setMessages: (interactionId: string, messages: InteractionMessage[]) =>
        send({ type: 'SET_MESSAGES', interactionId, messages }),
      addMessage: (message: InteractionMessage) => send({ type: 'ADD_MESSAGE', message }),
      markMessagesAsRead: (interactionId: string) =>
        send({ type: 'MARK_MESSAGES_AS_READ', interactionId }),
      setNotes: (interactionId: string, notes: InteractionNote[]) =>
        send({ type: 'SET_NOTES', interactionId, notes }),
      addNote: (note: InteractionNote) => send({ type: 'ADD_NOTE', note }),
      updateNote: (id: string, content: string) => send({ type: 'UPDATE_NOTE', id, content }),
      removeNote: (id: string) => send({ type: 'REMOVE_NOTE', id }),
      setActivities: (interactionId: string, activities: InteractionActivity[]) =>
        send({ type: 'SET_ACTIVITIES', interactionId, activities }),
      addActivity: (activity: InteractionActivity) => send({ type: 'ADD_ACTIVITY', activity }),
      setSelectedInteractionId: (id: string | null) =>
        send({ type: 'SET_SELECTED_INTERACTION_ID', id }),
      setFilters: (filters: Partial<InteractionFilters>) => send({ type: 'SET_FILTERS', filters }),
      clearFilters: () => send({ type: 'CLEAR_FILTERS' }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
      setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
      setStatistics: (statistics: InteractionStatistics) =>
        send({ type: 'SET_STATISTICS', statistics }),
      updateStatistics,
      reset: () => send({ type: 'RESET' }),
    }
  }, [machineApi])
}

export function useInteractionActions() {
  const machineApi = useInteractionListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setAnalysisResult: (interactionId: string, analysis: TicketAnalysis) =>
      send({ type: 'SET_ANALYSIS_RESULT', interactionId, analysis }),
    setAnalyzing: (interactionId: string, analyzing: boolean) =>
      send({ type: 'SET_ANALYZING', interactionId, analyzing }),
    setInteractions: (interactions: Interaction[]) =>
      send({ type: 'SET_INTERACTIONS', interactions }),
    addInteraction: (interaction: Interaction) => send({ type: 'ADD_INTERACTION', interaction }),
    updateInteraction: (id: string, updates: Partial<Interaction>) =>
      send({ type: 'UPDATE_INTERACTION', id, updates }),
    removeInteraction: (id: string) => send({ type: 'REMOVE_INTERACTION', id }),
    setMessages: (interactionId: string, messages: InteractionMessage[]) =>
      send({ type: 'SET_MESSAGES', interactionId, messages }),
    addMessage: (message: InteractionMessage) => send({ type: 'ADD_MESSAGE', message }),
    markMessagesAsRead: (interactionId: string) =>
      send({ type: 'MARK_MESSAGES_AS_READ', interactionId }),
    setNotes: (interactionId: string, notes: InteractionNote[]) =>
      send({ type: 'SET_NOTES', interactionId, notes }),
    addNote: (note: InteractionNote) => send({ type: 'ADD_NOTE', note }),
    updateNote: (id: string, content: string) => send({ type: 'UPDATE_NOTE', id, content }),
    removeNote: (id: string) => send({ type: 'REMOVE_NOTE', id }),
    setActivities: (interactionId: string, activities: InteractionActivity[]) =>
      send({ type: 'SET_ACTIVITIES', interactionId, activities }),
    addActivity: (activity: InteractionActivity) => send({ type: 'ADD_ACTIVITY', activity }),
    setSelectedInteractionId: (id: string | null) =>
      send({ type: 'SET_SELECTED_INTERACTION_ID', id }),
    setFilters: (filters: Partial<InteractionFilters>) => send({ type: 'SET_FILTERS', filters }),
    clearFilters: () => send({ type: 'CLEAR_FILTERS' }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
    setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
    setStatistics: (statistics: InteractionStatistics) =>
      send({ type: 'SET_STATISTICS', statistics }),
    reset: () => send({ type: 'RESET' }),
  }), [send])
}

export function useInteractions() {
  const compat = useInteractionListStoreCompat()
  return compat.interactions
}
